import {
  IChecklistCategory,
  IChecklistItem,
  IChecklistItemRow,
  IChecklistPopulated,
  TChecklistItemStatus,
} from "@mctypes/checklist.types";
import { IMinUserDepricated, IProjectDeprecated, IUserDepricated } from "@mctypes/global.types";
import { IChecklistReportData } from "@schemas/checklistSchema";
import { IIssueApi } from "@schemas/issueSchema";
import { IUser } from "@schemas/userSchema";
import moment from "moment";
import { getUserShortName, getUserShortNameForUsersNotInProject, getUserUpnFromEmail } from "../../utils";
import { doGenerateChecklistDocx } from "../admin/restQueries";

export const getChecklistTableRows = (
  editMode: boolean,
  checklist: IChecklistPopulated,
  checklistCategories: IChecklistCategory[],
  checklistItems: IChecklistItem[],
): IChecklistItemRow[] => {
  if (checklist == null || checklistCategories == null) {
    return [];
  }

  const tableRows: IChecklistItemRow[] = [];

  checklistCategories.forEach((cat) => {
    const catRow = {
      _id: cat._id,
      originalCategory: cat,
      dueDate: cat.dueDate,
      inactive: null,
      title: cat.title,
      reference: "",
      collapsed: cat.collapsed === true,
      originalItem: null,
      comment: null,
      checks: null,
      itemId: null,
      categoryId: cat.categoryId,
      rowId: cat.categoryId.toString(),
    };

    let catRows: IChecklistItemRow[] = [];
    let catRowInactive = true;
    let catHasItemRows;

    checklistItems.forEach((item: IChecklistItem) => {
      if (item.category === cat._id) {
        catHasItemRows = true;
        catRows.push({
          _id: item._id,
          originalCategory: cat,
          inactive: item.inactive,
          title: item.title,
          reference: item.reference ?? "",
          collapsed: cat.collapsed === true,
          originalItem: item,
          comment: item.comment,
          checks: {
            check1: item.checks?.check1,
            check2: item.checks?.check2,
            check3: item.checks?.check3,
            check4: item.checks?.check4,
            check5: item.checks?.check5,
            check6: item.checks?.check6,
            check7: item.checks?.check7,
            check8: item.checks?.check8,
            check9: item.checks?.check9,
            check10: item.checks?.check10,
            check11: item.checks?.check11,
            check12: item.checks?.check12,
            check13: item.checks?.check13,
            check14: item.checks?.check14,
            check15: item.checks?.check15,
          },
          itemId: item.itemId,
          categoryId: cat.categoryId,
          rowId: `${cat.categoryId}.${item.itemId}`,
          connectedIssues: item.connectedIssues as IIssueApi[],
          comments: item.comments,
          itemTags: item.itemTags ?? [],
        });

        if (item.inactive.isChecked === false) {
          catRowInactive = false;
        }
      }
    });

    catRow.inactive = {
      isChecked: catRowInactive === true && catHasItemRows === true,
      updatedDate: null,
      updatedBy: null,
    };

    if (
      editMode === false &&
      checklist.itemTagsActive === true &&
      checklist.activeItemTags.length > 0 &&
      catRows.length > 0
    ) {
      catRows = catRows.filter((row) => {
        // row should contain all active tags
        return checklist.activeItemTags.every((tag) => row.itemTags.includes(tag));
      });

      // if all rows are removed then remove category
      if (catRows.length === 0) {
        return;
      }
    }

    tableRows.push(catRow, ...catRows);
  });

  return tableRows;
};

export const getCategoryCount = (categories: IChecklistCategory[]): number => {
  if (categories == null || categories.length === 0) {
    return 0;
  }

  return categories.reduce((acc, category) => (acc > category.categoryId ? acc : category.categoryId), 0);
};

export const getItemCount = (categoryId: string, items: IChecklistItem[]): number => {
  if (items == null || items.length === 0) {
    return 0;
  }

  const categoryItems = items.filter((item) => item.category === categoryId);
  return categoryItems.reduce((acc, item) => (acc > item.itemId ? acc : item.itemId), 0);
};

const statusConverter = (v: TChecklistItemStatus, checklist: IChecklistPopulated) => {
  switch (v) {
    case "YES":
      return checklist.stampSettings.yes.text;
    case "NO":
      return checklist.stampSettings.no.text;
    case "IR":
      return checklist.stampSettings.ir.text;
    case "N/A":
      return checklist.stampSettings.na.text;
    case "UAV":
      return checklist.stampSettings.uav.text;
    default:
      return "-";
  }
};

export const generateChecklistReportDocxAndDownload = async (
  project: IProjectDeprecated,
  checklist: IChecklistPopulated,
  checklistCategories: IChecklistCategory[],
  checklistItems: IChecklistItem[],
  signedBy: IUserDepricated,
): Promise<void> => {
  const data = formatChecklistForDocxGeneration({
    project,
    checklist,
    checklistCategories,
    checklistItems,
    signedBy,
  });

  const types = [checklist.checkTypes.check1, checklist.checkTypes.check2, checklist.checkTypes.check3].filter(
    (v) => v.active === true,
  );
  const count = types.filter((v) => v.active === true).length;
  const templateName = `checklist_${count}col.docx`;
  await doGenerateChecklistDocx(data, templateName, `${project.maconomy.name} - ${checklist.name}`);
};

interface IArgsFormatChecklistDataForDocxGeneration {
  project: IProjectDeprecated;
  checklist: IChecklistPopulated;
  checklistCategories: IChecklistCategory[];
  checklistItems: IChecklistItem[];
  signedBy: IUserDepricated;
}

const isAllChecklistItemsInACategoryIR = (category: IChecklistCategory, checklistItems: IChecklistItem[]): boolean => {
  const itemsInCategory = checklistItems.filter((item) => item.category === category._id);
  if (itemsInCategory.length === 0) {
    return false;
  }
  return itemsInCategory.every((item) => item.inactive?.isChecked === true);
};

export const formatChecklistForDocxGeneration = (
  args: IArgsFormatChecklistDataForDocxGeneration,
): IChecklistReportData => {
  const dkApprovedAt = args.checklist.doksam?.dkApproval?.approvedAt ?? null;
  const hasDkApproval = dkApprovedAt != null;
  const types = [args.checklist.checkTypes.check1, args.checklist.checkTypes.check2, args.checklist.checkTypes.check3];
  const typeTitles = [
    args.checklist.checkTypes.check1.name,
    args.checklist.checkTypes.check2.name,
    args.checklist.checkTypes.check3.name,
  ];

  const colTitles = types.reduce((acc, curr, i) => {
    if (curr) {
      acc.push(typeTitles[i]);
    }

    return acc;
  }, []);

  const hasAboutText = args.checklist.aboutText != null && args.checklist.aboutText !== "";

  const data: IChecklistReportData = {
    id: args.checklist.id,
    checklistTitle: args.checklist.name,
    shortDate: moment(new Date()).format("DD-MM-YYYY"),
    projectNo: args.project.maconomy.projectNo,
    projectName: args.project.maconomy.name,
    cOneNames: "",
    cOneShortNames: "",
    cTwoNames: "",
    cTwoShortNames: "",
    cThreeNames: "",
    cThreeShortNames: "",
    checks: "",
    cOne: colTitles[0],
    cTwo: colTitles[1],
    cThree: colTitles[2],
    categories: [],
    hasAboutText,
    aboutText: args.checklist.aboutText != null ? `\n${args.checklist.aboutText}` : "",
    phase: args.checklist.phase ?? "",
    discipline: args.checklist.discipline ?? "",
    activity: args.checklist.activity ?? "",
    preparedBy: args.checklist.documentManagers?.preparedBy?.name ?? "",
    checkedBy: args.checklist.documentManagers?.checkedBy?.name ?? "",
    approvedBy: args.checklist.documentManagers?.approvedBy?.name ?? "",
    hasItemTags:
      args.checklist.itemTagsActive === true &&
      args.checklist.activeItemTags != null &&
      args.checklist.activeItemTags.length > 0,
    revNr: args.checklist.revisionNr ?? "",
    revDate: args.checklist.revisionDate ?? "",
    signedBy: args.signedBy.name ?? "",
    cDkCoreName: hasDkApproval ? (args.checklist.doksam?.dkApproval?.name ?? "") : "",
    cDkCoreShortName: hasDkApproval ? (args.checklist.doksam?.dkApproval?.upn ?? "") : "",
  };

  if (
    args.checklist.itemTagsActive === true &&
    args.checklist.activeItemTags != null &&
    args.checklist.activeItemTags.length > 0
  ) {
    data.activeItemTags = `${hasAboutText ? "\n" : ""}Etikettfilter: ${args.checklist.activeItemTags.join(", ")}`;
    data.activeItemTagChangedBy =
      typeof args.checklist.activeItemTagChangedBy === "string"
        ? ""
        : args.checklist.activeItemTagChangedBy?.name ?? "";
    data.activeItemTagDate = moment(args.checklist.activeItemTagChangedDate).format("DD-MM-YYYY");
  }

  const check1NamesSet = new Set<string>();
  const check2NamesSet = new Set<string>();
  const check3NamesSet = new Set<string>();
  const check1ShortNamesSet = new Set<string>();
  const check2ShortNamesSet = new Set<string>();
  const check3ShortNamesSet = new Set<string>();

  args.checklistCategories.forEach((cat) => {
    const categoryRow = {
      catId: cat.categoryId,
      categoryTitle: cat.title,
      rows: [],
    };

    const allIR = isAllChecklistItemsInACategoryIR(cat, args.checklistItems);
    if (allIR === true) {
      return;
    }

    categoryRow.rows = args.checklistItems.reduce((acc, checklistItem) => {
      if (
        args.checklist.itemTagsActive === true &&
        args.checklist.activeItemTags.every((tag) => checklistItem.itemTags.includes(tag)) === false
      ) {
        return acc;
      }

      if (checklistItem.category === cat._id) {
        const checks =
          checklistItem.inactive.isChecked === true
            ? [
                args.checklist.stampSettings.ir.text,
                args.checklist.stampSettings.ir.text,
                args.checklist.stampSettings.ir.text,
              ]
            : [
                statusConverter(checklistItem.checks.check1.status, args.checklist),
                statusConverter(checklistItem.checks.check2.status, args.checklist),
                statusConverter(checklistItem.checks.check3.status, args.checklist),
              ];

        const rowContent = types.reduce((typeAcc, curr, i) => {
          if (curr) {
            typeAcc.push(checks[i]);
          }
          return typeAcc;
        }, []);

        // Checks 4 - 15 is not exported to report
        let nameOne = "";
        let nameTwo = "";
        let nameThree = "";

        if (
          checklistItem.checks.check1.status !== "N/A" &&
          typeof checklistItem.checks.check1.updatedBy !== "string" &&
          checklistItem.checks.check1 != null
        ) {
          check1NamesSet.add(checklistItem.checks.check1.updatedBy?.name ?? "");
          nameOne = checklistItem.checks.check1.updatedBy?.name ?? "";
          const user = args.project.members.find(
            (member) =>
              (member.user as IMinUserDepricated)._id ===
              (checklistItem.checks.check1.updatedBy as IMinUserDepricated)?._id,
          );
          const shortName = user
            ? getUserShortName(user.user as IUser)
            : getUserShortNameForUsersNotInProject(checklistItem.checks.check1.updatedBy?.name ?? "");
          check1ShortNamesSet.add(shortName);
        }

        if (
          checklistItem.checks.check2.status !== "N/A" &&
          typeof checklistItem.checks.check2.updatedBy !== "string" &&
          checklistItem.checks.check2 != null
        ) {
          check2NamesSet.add(checklistItem.checks.check2.updatedBy?.name ?? "");
          nameTwo = checklistItem.checks.check2.updatedBy?.name ?? "";
          const user = args.project.members.find(
            (member) =>
              (member.user as IMinUserDepricated)._id ===
              (checklistItem.checks.check2.updatedBy as IMinUserDepricated)?._id,
          );
          const shortName = user
            ? getUserShortName(user.user as IUser)
            : getUserShortNameForUsersNotInProject(checklistItem.checks.check2.updatedBy?.name ?? "");
          check2ShortNamesSet.add(shortName);
        }

        if (
          checklistItem.checks.check3.status !== "N/A" &&
          typeof checklistItem.checks.check3.updatedBy !== "string" &&
          checklistItem.checks.check3 != null
        ) {
          check3NamesSet.add(checklistItem.checks.check3.updatedBy?.name ?? "");
          nameThree = checklistItem.checks.check3.updatedBy?.name ?? "";
          const user = args.project.members.find(
            (member) =>
              (member.user as IMinUserDepricated)._id ===
              (checklistItem.checks.check3.updatedBy as IMinUserDepricated)?._id,
          );
          const shortName = user
            ? getUserShortName(user.user as IUser)
            : getUserShortNameForUsersNotInProject(checklistItem.checks.check3.updatedBy?.name ?? "");
          check3ShortNamesSet.add(shortName);
        }

        if (checklistItem.inactive.isChecked === true && typeof checklistItem.inactive.updatedBy !== "string") {
          check1NamesSet.add(checklistItem.inactive.updatedBy?.name ?? "");
          nameOne = checklistItem.inactive.updatedBy?.name ?? "";
        }

        acc.push({
          rowId: `${cat.categoryId}.${checklistItem.itemId}`,
          rowTitle: checklistItem.title,
          rowReference: checklistItem.reference?.length > 0 ? `\n${checklistItem.reference}` : "",
          rOne: rowContent[0],
          rTwo: rowContent[1],
          rThree: rowContent[2],
          comment: checklistItem.comment,
          nameOne,
          nameTwo,
          nameThree,
        });
      }

      return acc;
    }, []);

    data.categories.push(categoryRow);
  });

  const getNamesFromSet = (checkNames: Set<string>) => {
    let names = "";
    let count = 0;

    checkNames.forEach((name) => {
      names += count > 0 ? `, ${name}` : name;
      count += 1;
    });

    return names;
  };

  data.cOneNames = getNamesFromSet(check1NamesSet);
  data.cOneShortNames = getNamesFromSet(check1ShortNamesSet);
  data.cTwoNames = getNamesFromSet(check2NamesSet);
  data.cTwoShortNames = getNamesFromSet(check2ShortNamesSet);
  data.cThreeNames = getNamesFromSet(check3NamesSet);
  data.cThreeShortNames = getNamesFromSet(check3ShortNamesSet);

  data.checks = `${args.checklist.stampSettings.yes.text}/${args.checklist.stampSettings.no.text}/${args.checklist.stampSettings.na.text}`;
  return data;
};

export const loggedinUserIsEkInDoksamChecklist = (checklist: IChecklistPopulated, email: string): boolean => {
  return getUserUpnFromEmail(email) === checklist.doksam.check1UserUpn?.toLowerCase();
};

export const loggedinUserIsSKInDoksamChecklist = (checklist: IChecklistPopulated, email: string): boolean => {
  return getUserUpnFromEmail(email) === checklist.doksam.check2UserUpn?.toLowerCase();
};

interface IStatsForCheck {
  checked: number;
  total: number;
}

export const getStatsForCheck = (checklistItems: IChecklistItem[], checkTitle: "check1" | "check2"): IStatsForCheck => {
  return checklistItems.reduce(
    (acc, item) => {
      if (item.checks[checkTitle]) {
        acc.total += 1;
        if (item.checks[checkTitle].status !== "N/A" || item.inactive.isChecked === true) {
          acc.checked += 1;
        }
      }
      return acc;
    },
    { checked: 0, total: 0 },
  );
};
